/**
 * The filter row above every history table.
 *
 * Replaces four near-identical copies of a date picker, a status select and two
 * text inputs. The two inputs become one token box (`make:toyota -tier:tier1`)
 * because the alternative — an input per searchable field — does not fit on a
 * screen once there are more than about three, and every new field would have
 * meant editing four components.
 *
 * Source and Tier stay dropdowns: they have short closed vocabularies, and a
 * list you can see beats a token you have to remember. They ride the same
 * backend path as the tokens, so `source:manual` in the box and Manual in the
 * dropdown mean the same query.
 *
 * The bar renders only what the application records — see `historyQuery.ts` —
 * so it never offers a filter the table cannot answer. That goes for what it
 * says as well as what it accepts: the suggestion list, the placeholder and the
 * `?` panel are all generated from the same catalog.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { DateRange } from '../types';
import { DateRangePicker } from './DateRangePicker';
import { HistoryFilterHelp } from './HistoryFilterHelp';
import {
  describeSuggestion,
  fieldsFor,
  hasField,
  parseQuery,
  suggestQuery,
  unknownTerms,
  type HistoryApp,
  type StatusOption,
  SCAN_STATUS_OPTIONS,
  SOURCE_VALUES,
  TIER_VALUES,
} from './historyQuery';

const SOURCE_LABELS: Record<string, string> = {
  manual: 'Manual',
  uvcamp: 'UVCamp',
  stress: 'Stress',
  benchmark: 'Benchmark',
};

const TIER_LABELS: Record<string, string> = {
  tier0: 'Tier 0',
  tier1: 'Tier 1',
  tier2: 'Tier 2',
  tier3: 'Tier 3',
  tier4: 'Tier 4',
};

export interface HistoryFilters {
  dateRange: DateRange;
  status: string | null;
  source: string | null;
  tier: string | null;
  q: string;
}

export interface HistoryFilterBarProps {
  app: HistoryApp;
  value: HistoryFilters;
  onChange: (next: HistoryFilters) => void;
  /** Re-runs the current filters. The box does not search as you type. */
  onApply: () => void;
  statusOptions?: StatusOption[];
  /** Rendered at the end of the row — normally the export button. */
  children?: React.ReactNode;
  /**
   * Terms the server could not use. Surfaced next to the box so an unanswerable
   * query is visible rather than looking like an empty result.
   */
  ignoredTerms?: string[];
}

export function HistoryFilterBar({
  app,
  value,
  onChange,
  onApply,
  statusOptions = SCAN_STATUS_OPTIONS,
  children,
  ignoredTerms,
}: HistoryFilterBarProps) {
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  const showTier = hasField(app, 'tier');
  // Uncapped: the list is the only place the searchable surface is enumerated,
  // and merch has sixteen fields, so a cap of eight hid half of them behind
  // guessing the first letter. The dropdown scrolls instead.
  const suggestions = useMemo(
    () => (suggestionsOpen ? suggestQuery(app, value.q) : []),
    [app, value.q, suggestionsOpen],
  );
  // Warn locally while typing; the server's own list covers values it rejected.
  const unknown = useMemo(
    () => (ignoredTerms?.length ? ignoredTerms : unknownTerms(app, value.q)),
    [app, value.q, ignoredTerms],
  );
  const termCount = useMemo(() => parseQuery(value.q).length, [value.q]);
  // Named after this application's own free-text fields, so the box never
  // invites a search it would then report as ignored. `source:` is the one
  // token example every application can honour.
  const placeholder = useMemo(() => {
    const bare = fieldsFor(app)
      .filter((field) => field.freeText)
      .map((field) => field.label.toLowerCase())
      .join(', ');
    return `Search ${bare} — or source:manual, -source:stress`;
  }, [app]);

  useEffect(() => {
    setHighlighted(0);
  }, [value.q]);

  useEffect(() => {
    if (!suggestionsOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setSuggestionsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [suggestionsOpen]);

  const update = (patch: Partial<HistoryFilters>) => onChange({ ...value, ...patch });

  const accept = (suggestion: string) => {
    // A field suggestion ends in ':' and still needs a value, so keep the list
    // open; a completed value is a whole term, so add the separating space.
    update({ q: suggestion.endsWith(':') ? suggestion : `${suggestion} ` });
    setSuggestionsOpen(suggestion.endsWith(':'));
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : suggestions.length - 1;
      setHighlighted((current) => (current + step) % suggestions.length);
      return;
    }
    if (event.key === 'Escape') {
      setSuggestionsOpen(false);
      return;
    }
    if (event.key === 'Tab' && suggestions.length > 0) {
      event.preventDefault();
      accept(suggestions[highlighted]);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (suggestionsOpen && suggestions.length > 0) {
        accept(suggestions[highlighted]);
        return;
      }
      onApply();
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 sm:gap-3 flex-nowrap sm:flex-wrap overflow-x-auto pb-1 sm:pb-0 scrollbar-hide">
        <DateRangePicker value={value.dateRange} onChange={(dateRange) => update({ dateRange })} />

        <div className="flex items-center gap-2 shrink-0">
          <label className="text-xs text-slate-500 hidden sm:block">Status:</label>
          <select
            value={value.status || ''}
            onChange={(event) => update({ status: event.target.value || null })}
            className="input-pill"
            title="Status filter"
          >
            <option value="">All Status</option>
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <label className="text-xs text-slate-500 hidden sm:block">Source:</label>
          <select
            value={value.source || ''}
            onChange={(event) => update({ source: event.target.value || null })}
            className="input-pill"
            title="Source filter"
          >
            <option value="">All Sources</option>
            {SOURCE_VALUES.map((source) => (
              <option key={source} value={source}>
                {SOURCE_LABELS[source]}
              </option>
            ))}
          </select>
        </div>

        {showTier && (
          <div className="flex items-center gap-2 shrink-0">
            <label className="text-xs text-slate-500 hidden sm:block">Tier:</label>
            <select
              value={value.tier || ''}
              onChange={(event) => update({ tier: event.target.value || null })}
              className="input-pill"
              title="Generation tier filter"
            >
              <option value="">All Tiers</option>
              {TIER_VALUES.map((tier) => (
                <option key={tier} value={tier}>
                  {TIER_LABELS[tier]}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 sm:gap-3 flex-nowrap sm:flex-wrap">
        <div ref={boxRef} className="relative flex-1 w-0 min-w-0 sm:min-w-[18rem]">
          <input
            type="text"
            value={value.q}
            onChange={(event) => {
              update({ q: event.target.value });
              setSuggestionsOpen(true);
            }}
            onFocus={() => setSuggestionsOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            title="Search by field. Type a field name for suggestions; Enter applies."
            className="input-pill w-full"
            aria-label="Search history"
            aria-expanded={suggestionsOpen && suggestions.length > 0}
            role="combobox"
            aria-controls="history-query-suggestions"
          />
          {suggestionsOpen && suggestions.length > 0 && (
            <ul
              id="history-query-suggestions"
              role="listbox"
              className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
            >
              {suggestions.map((suggestion, index) => {
                const { token, label, hint } = describeSuggestion(app, suggestion);
                return (
                  <li key={suggestion} role="option" aria-selected={index === highlighted}>
                    <button
                      type="button"
                      tabIndex={-1}
                      onMouseEnter={() => setHighlighted(index)}
                      onClick={() => accept(suggestion)}
                      className={`w-full px-3 py-1.5 text-left ${
                        index === highlighted ? 'bg-indigo-50' : ''
                      }`}
                    >
                      <span className="flex items-baseline justify-between gap-3">
                        <span
                          className={`font-mono text-xs ${
                            index === highlighted ? 'text-indigo-700' : 'text-slate-600'
                          }`}
                        >
                          {token}
                        </span>
                        {label && (
                          <span className="text-[11px] text-slate-400 shrink-0">{label}</span>
                        )}
                      </span>
                      {hint && (
                        <span className="block text-[10px] text-slate-400 mt-0.5">{hint}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <button
          type="button"
          onClick={() => setHelpOpen((open) => !open)}
          title="How the search works"
          aria-label="How the search works"
          aria-expanded={helpOpen}
          className={`shrink-0 w-6 h-6 rounded-full border text-xs font-semibold ${
            helpOpen
              ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
              : 'border-slate-200 text-slate-400 hover:text-slate-600'
          }`}
        >
          ?
        </button>

        <button
          onClick={onApply}
          title="Apply Filters"
          className="btn-secondary px-2.5 py-1 rounded-md text-xs flex items-center gap-1.5 shrink-0"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          <span className="hidden sm:inline">Apply Filters</span>
          {termCount > 0 && <span className="text-slate-400">({termCount})</span>}
        </button>

        {children && <div className="shrink-0">{children}</div>}
      </div>

      {unknown.length > 0 && (
        <p className="text-xs text-amber-600">
          Ignored, because this history records no such field:{' '}
          <span className="font-mono">{unknown.join(' ')}</span>. Press ? for the fields it does
          record.
        </p>
      )}

      {helpOpen && <HistoryFilterHelp app={app} />}
    </div>
  );
}
